import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CupSoda, Download, MapPin, MessageCircle, Phone, UtensilsCrossed } from "lucide-react";
import { SITE } from "../data/menu";
import { Flourish, Lantern, PetalField, Blossom } from "../components/decor";
import { Reveal } from "../components/Reveal";

const TOTAL_PAGES = 29;
const FOOD_PAGES = Array.from({ length: TOTAL_PAGES }, (_, i) => `/menu-book/page-${String(i + 1).padStart(2, "0")}.jpg`);
const DRINKS_PAGES = ["/menu-book/drinks-01.jpg"];

const MENUS = {
  food: {
    label: "Food Menu",
    pdf: "/menu-book/bloomora-menu.pdf",
    file: "Bloomora-Food-Menu.pdf",
    note: "Full 29-page menu book — same as on your table",
    pages: FOOD_PAGES,
  },
  drinks: {
    label: "Beverages & Shakes",
    pdf: "/menu-book/bloomora-drinks.pdf",
    file: "Bloomora-Drinks-Menu.pdf",
    note: "Shakes, coolers & coffee — the sippable chapter",
    pages: DRINKS_PAGES,
  },
};

const DRINK_GROUPS = [
  {
    group: "Milkshakes & Shakes",
    items: [
      ["Butterscotch Shake", 129], ["Chocolate Shake", 129], ["Oreo Shake", 139],
      ["KitKat Shake", 149], ["Cold Coffee Shake", 139], ["Dry Fruit Shake", 169],
      ["Vanilla Shake", 119], ["Nutella Milkshake", 159], ["Strawberry Shake", 129],
    ],
  },
  {
    group: "Hot & Cold Drinks",
    items: [["Hot Coffee", 60], ["Cold Coffee", 139], ["Cold Coffee with Ice Cream", 169]],
  },
];

const MenuPageImage = ({ src, index, total }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <motion.figure
      data-testid={`menu-page-${index + 1}`}
      className="relative overflow-hidden rounded-[12px] bg-white/70 shadow-[0_10px_36px_rgba(155,44,44,0.10)]"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "200px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      {!loaded && (
        <div className="aspect-[1191/1684] w-full animate-pulse bg-[#F3D9D9]" aria-hidden="true" />
      )}
      <img
        src={src}
        alt={`Bloomora menu page ${index + 1}`}
        loading={index < 2 ? "eager" : "lazy"}
        decoding="async"
        onLoad={() => setLoaded(true)}
        className={`w-full transition-opacity duration-500 ${loaded ? "opacity-100" : "absolute inset-0 opacity-0"}`}
      />
      <figcaption className="absolute bottom-3 right-3 rounded-full bg-[#F8E8E8]/85 px-3 py-1 text-[11px] font-medium text-[#9B2C2C] backdrop-blur-sm">
        {index + 1} / {total}
      </figcaption>
    </motion.figure>
  );
};

const MenuActionBar = () => (
  <div
    data-testid="menu-action-bar"
    className="fixed bottom-0 left-0 z-50 w-full border-t border-[#E8B4B8]/40 bg-[#F8E8E8]/95 py-3 backdrop-blur-xl md:py-4"
  >
    <div className="mx-auto flex max-w-xl items-center justify-around gap-2 px-4 md:justify-center md:gap-6">
      <a
        href={SITE.phoneHref}
        data-testid="menu-bar-call"
        className="group relative inline-flex items-center gap-2 rounded-full bg-[#9B2C2C] px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-[#7E2222] md:px-7 md:text-sm"
      >
        <Phone size={15} /> Call for Reservation
        <span className="absolute bottom-1.5 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-[#C9A227] transition-[width] duration-300 group-hover:w-1/2" />
      </a>
      <a
        href={SITE.whatsapp}
        target="_blank"
        rel="noreferrer"
        data-testid="menu-bar-whatsapp"
        className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-xs font-semibold text-white transition-transform hover:scale-105 md:px-7 md:text-sm"
      >
        <MessageCircle size={15} /> WhatsApp
      </a>
      <a
        href={SITE.mapsLink}
        target="_blank"
        rel="noreferrer"
        data-testid="menu-bar-directions"
        className="inline-flex items-center gap-2 rounded-full border-2 border-[#C9A227] px-5 py-2 text-xs font-semibold text-[#9B2C2C] transition-colors hover:bg-[#C9A227] hover:text-white md:px-7 md:text-sm"
      >
        <MapPin size={15} /> Directions
      </a>
    </div>
  </div>
);

export default function MenuPage() {
  const [tab, setTab] = useState("food");
  const viewerRef = useRef(null);
  const active = MENUS[tab];

  const switchTab = (t) => {
    if (t === tab) return;
    setTab(t);
    const el = viewerRef.current;
    if (el) {
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -160, duration: 0.9 });
      else window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 160, behavior: "smooth" });
    }
  };

  useEffect(() => {
    document.title = "Menu — Bloomora Café & Restaurant";
    return () => { document.title = "Bloomora Café & Restaurant"; };
  }, []);

  return (
    <main data-testid="menu-page" className="pb-24 md:pb-28">
      <section className="relative overflow-hidden px-5 pb-10 pt-16 text-center md:pt-20">
        <PetalField count={8} />
        <div className="pointer-events-none absolute right-6 top-0 hidden md:block">
          <Lantern className="h-28 w-14" />
        </div>
        <div className="pointer-events-none absolute left-6 top-0 hidden md:block">
          <Lantern className="h-24 w-12" delay={1.4} />
        </div>

        <Reveal>
          <div className="flex items-center justify-center gap-3">
            <Blossom className="h-7 w-7 text-[#E8B4B8]" />
            <h1 className="font-serif text-4xl font-black tracking-tight text-[#9B2C2C] md:text-5xl">BLOOMORA</h1>
            <Blossom className="h-7 w-7 text-[#E8B4B8]" />
          </div>
          <p className="font-hindi mt-1 text-lg text-[#B86B77]">ब्लूमोरा</p>
        </Reveal>

        <Reveal delay={0.1}>
          <p
            data-testid="menu-tagline-main"
            className="mt-5 font-serif text-xl font-bold uppercase tracking-[0.2em] text-[#9B2C2C] md:text-2xl"
          >
            Eat <span className="text-[#C9A227]">Bloom</span> Repeat
          </p>
          <p data-testid="menu-tagline-sub" className="mt-2 text-xs font-medium uppercase tracking-[0.3em] text-[#B86B77] md:text-sm">
            Good Food. Good Mood. Good Times.
          </p>
          <div className="mt-5 flex justify-center"><Flourish className="h-4 w-56" /></div>
        </Reveal>

        <Reveal delay={0.16}>
          <div
            data-testid="menu-type-tabs"
            className="mt-8 inline-flex rounded-full border border-[#E8B4B8]/50 bg-white/70 p-1.5 shadow-[0_6px_20px_rgba(155,44,44,0.08)] backdrop-blur-sm"
          >
            {Object.entries(MENUS).map(([key, m]) => (
              <button
                key={key}
                data-testid={`menu-tab-${key}`}
                onClick={() => switchTab(key)}
                className={`relative rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 md:px-7 ${
                  tab === key ? "text-white" : "text-[#9B2C2C] hover:text-[#7E2222]"
                }`}
              >
                {tab === key && (
                  <motion.span
                    layoutId="menu-type-pill"
                    className="absolute inset-0 rounded-full bg-[#9B2C2C]"
                    transition={{ type: "spring", stiffness: 320, damping: 30 }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-2">
                  {key === "food" ? <UtensilsCrossed size={15} /> : <CupSoda size={15} />}
                  {m.label}
                </span>
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.18}>
          <a
            key={tab}
            href={active.pdf}
            download={active.file}
            target="_blank"
            rel="noreferrer"
            data-testid="download-menu-button"
            className="group relative mt-8 inline-flex items-center gap-2.5 rounded-full bg-[#9B2C2C] px-9 py-4 font-semibold text-white shadow-[0_12px_32px_rgba(155,44,44,0.3)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#7E2222] hover:shadow-[0_18px_44px_rgba(155,44,44,0.35)]"
          >
            <Download size={18} />
            {`Download ${tab === "food" ? "Food" : "Drinks"} Menu (PDF)`}
            <span className="absolute bottom-2.5 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-[#C9A227] transition-[width] duration-300 group-hover:w-1/2" />
          </a>
          <p className="mt-3 text-xs text-[#B86B77]">{active.note}</p>
        </Reveal>
      </section>

      <section ref={viewerRef} data-testid="menu-viewer" className="mx-auto flex max-w-3xl flex-col gap-5 px-4 md:gap-7 md:px-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-5 md:gap-7"
          >
            {tab === "drinks" && (
              <div data-testid="drinks-quick-list" className="grid gap-5 sm:grid-cols-2">
                {DRINK_GROUPS.map((g) => (
                  <div
                    key={g.group}
                    className="rounded-[12px] border border-[#E8B4B8]/30 bg-white/80 p-6 shadow-[0_6px_24px_rgba(155,44,44,0.07)] backdrop-blur-sm"
                  >
                    <h3 className="font-serif text-lg font-semibold tracking-wide text-[#C9A227]">{g.group}</h3>
                    <ul className="mt-4 flex flex-col gap-3">
                      {g.items.map(([name, price]) => (
                        <li key={name} className="flex items-center justify-between gap-3">
                          <span className="text-sm font-medium text-[#2C2C2C]">{name}</span>
                          <span className="shrink-0 rounded-full bg-[#9B2C2C] px-3 py-1 font-serif text-sm font-semibold text-white">
                            ₹{price}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            {tab === "food" &&
              active.pages.map((src, i) => (
                <MenuPageImage key={src} src={src} index={i} total={active.pages.length} />
              ))}
          </motion.div>
        </AnimatePresence>
      </section>

      <Reveal className="mt-14 text-center">
        <Flourish className="mx-auto h-4 w-52" />
        <p className="mt-4 font-serif text-lg italic text-[#9B2C2C]">Bloomora – Where every meal blooms</p>
      </Reveal>

      <MenuActionBar />
    </main>
  );
}
